import React, { forwardRef, useState, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
	Autocomplete,
	Button,
	Paper,
	Stack,
	Checkbox,
	TextField,
	Popper,
	Typography,
} from '@mui/material';
import styles from './MultiItemDropDown.module.css';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { colors } from '../../theme/color-palette';

const MultiItemDropDown = forwardRef((props, ref) => {
	MultiItemDropDown.displayName = 'MultiItemDropDown';

	useImperativeHandle(ref, () => ({
		setToDefaultState() {
			setTempState(options);
		},
	}));

	const CustomPopper = React.useCallback((props) => {
		const elements = document.querySelectorAll('[data-testid=mti-drop-down]');
		const firstElement = elements[0];
		return (
			<Popper {...props} placement="bottom-start" anchorEl={firstElement} />
		);
	}, []);

	// Defining static icons components
	const icon = <CheckBoxOutlineBlank fontSize="small" />;
	const checkedIcon = <CheckBox fontSize="small" />;

	let {
		options,
		disabledOptionIndex,
		isClearable,
		selected,
		onChange,
		onClear,
		placeholder,
		label,
		value,
	} = props;

	disabledOptionIndex = disabledOptionIndex || 0;

	const [tempState, setTempState] = useState(value);

	const renderOneItemWithPlus = (itemsArray) => {
		// If value is an array and one of the array items is having value 'ALL'
		if (
			itemsArray.length == options.length ||
			itemsArray.some((obj) => obj.value === 'ALL')
		) {
			return 'All';
		} else if (itemsArray.length > 1) {
			return itemsArray[0].shortLabel + ' +' + (itemsArray.length - 1);
		} else if (itemsArray.length === 1) {
			return itemsArray[0].shortLabel;
		} else {
			return '';
		}
	};

	const isAllSelected = (value) => {
		// If value is an array and one of the array items is having value 'ALL'
		if (Array.isArray(value) && value.some((obj) => obj.value === 'ALL')) {
			// All element is selected, Then we will set the state to all same as options
			return true;
		} else {
			// All element is not selected, Then we will set the state to empty
			return false;
		}
	};

	return (
		<Autocomplete
			defaultValue={selected}
			isOptionEqualToValue={(option, value) => option.value === value.value}
			PaperComponent={({ children }) => {
				return (
					<Paper
						sx={{
							boxShadow:
								'0px -1px 4px 0px rgba(0, 0, 0, 0.10), 0px 5px 10px 3px rgba(0, 0, 0, 0.15)',

							'& .MuiAutocomplete-listbox li ': {
								height: '36px',
							},
						}}
						className={styles.paperComponent}>
						{children}
						<Stack
							direction="row"
							spacing={2}
							justifyContent="flex-end"
							alignItems="center"
							alignContent="flex-end"
							sx={{
								borderTop: '1px solid #f3f3f3',
								padding: '8px',
							}}>
							<Button
								data-testid="mti-cancel"
								className={styles.cancelBtn}
								onMouseDown={() => {
									setTempState(value);
								}}>
								Cancel
							</Button>

							<Button
								data-testid="mti-apply"
								className={styles.applyBtn}
								onMouseDown={() => {
									onChange(tempState);
								}}>
								Apply
							</Button>
						</Stack>
					</Paper>
				);
			}}
			multiple
			disableClearable={!isClearable}
			onClear={onClear}
			disableCloseOnSelect
			options={options}
			getOptionDisabled={(option) => option === disabledOptionIndex}
			getOptionLabel={(option) => option.shortLabel}
			data-testid="mti-drop-down"
			value={tempState}
			onChange={(event, newValue, situation, option) => {
				if (situation === 'removeOption') {
					// If All is unchecked, Then we need to remove all checkmarks
					if (option.option?.value === 'ALL') {
						setTempState([]);
						return;
					}

					// If All items are checked, Then we need to automatically check the All item too
					if (isAllSelected(newValue)) {
						const optionsWithoutAll = newValue.filter(
							(el) => el.value != 'ALL'
						);
						setTempState(optionsWithoutAll);
						return;
					} else {
						setTempState(newValue);
						return;
					}
					// If All is selected, Then we will select all the available options
				} else if (isAllSelected(newValue)) {
					setTempState(options);
				} else {
					// Otherwise, Set the selected items only
					setTempState(newValue);
				}
			}}
			renderTags={(tagValue) => renderOneItemWithPlus(tagValue)}
			PopperComponent={CustomPopper}
			renderOption={(props, option, { selected }) => (
				<li
					className={styles.listCheckItem}
					{...props}
					data-testid={`mti-option-${option.value}`}>
					<Checkbox
						disabled
						icon={icon}
						checkedIcon={checkedIcon}
						checked={selected}
						sx={{ color: selected && `${colors.PURPLE} !important` }}
					/>
					<Typography
						sx={{
							fontSize: '14px',
						}}>
						{option.title}
					</Typography>
				</li>
			)}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						sx={{
							width: 'fit-content',
							padding: '9px 16px',
							'& .MuiInputBase-root': {
								padding: 0,
								':hover': {
									cursor: 'pointer',
								},
							},
						}}
						InputProps={{
							...params.InputProps,
							readOnly: true,
							startAdornment: `${label} ${
								params.InputProps.startAdornment || ''
							}`,
						}}
						className={styles.textFieldMultiItem}
						placeholder={options?.length === 0 ? placeholder : ''}
					/>
				);
			}}></Autocomplete>
	);
});

export default MultiItemDropDown;

MultiItemDropDown.propTypes = {
	isClearable: PropTypes.bool,
	disabledOptionIndex: PropTypes.number,
	options: PropTypes.array,
	selected: PropTypes.array,
	onChange: PropTypes.func,
	onClear: PropTypes.func,
	placeholder: PropTypes.string,
	label: PropTypes.string,
	value: PropTypes.array,
};
